import * as React from "react";
import {
  BlockTypes,
  ColorNamesBlockModel,
  ColorNamesColorPresetModel,
  ColorNamesTextBlockNum,
  GraphicNames12CutTextBlockNum,
  GraphicNamesTextBlockNum,
  ImageDirection,
  SwitchableBlockModel,
} from "../../models/BlockTransportSystemJsonModel";
import { CNSSetLayoutStyles } from "../../models/CNSSetLayoutStyles";
import { Button, CloseButton, Col, FloatingLabel, Form, Row, Image, Container, Badge, Dropdown, ButtonGroup, Stack } from "react-bootstrap";
import BlockPreviewImageModel from "../../models/BlockPreviewImageModel";
import { BlockPresetConfig } from "../../constants/BlockPresetConfig";
import ModalTextColorPreset from "../ModalTextColorPreset/ModalTextColorPreset";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

export interface IBlockPreviewImageBoxProps {
  previewImage: BlockPreviewImageModel
  isColorNamesType: boolean;
  isUseTextColorPresets: boolean;
  presetConfig?: BlockPresetConfig;
  blockType: BlockTypes;
  colorNamesLayout?: CNSSetLayoutStyles;
  deleteBtnClicked: (e: React.MouseEvent<HTMLButtonElement>) => void;
  onPresetCreate: (previewModel: ColorNamesColorPresetModel) => void;
  onChangeColorNamesLayout: (direction: string) => void;
  onPresetDelete: (index: number) => void;
  onPresetEdit: (index: number, previewModel: ColorNamesColorPresetModel) => void;
  updatePreviewImage: (id: string, changes: Partial<BlockPreviewImageModel>) => void;
}

export interface IBlockPreviewImageBoxState { }

const BlockPreviewImageBox = ({
  previewImage,
  isColorNamesType,
  isUseTextColorPresets,
  presetConfig,
  blockType,
  colorNamesLayout,
  deleteBtnClicked,
  onChangeColorNamesLayout,
  onPresetCreate,
  onPresetDelete,
  onPresetEdit,
  updatePreviewImage,
}: IBlockPreviewImageBoxProps) => {
  const [isShowTextColorModal, setIsShowTextColorModal] = useState<boolean>(false);
  const [textColorPreset, setTextColorPreset] = useState<ColorNamesColorPresetModel>();
  const [textColorPresetIndex, setTextColorPresetIndex] = useState<number>(0);
  const [isCreateTextPreset, setIsCreateTextPreset] = useState<boolean>(false);
  const { t } = useTranslation();

  const handleFileOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) {
      return null;
    }

    let file = e.target.files[0];
    if (!file) return Promise.resolve(null);
    
    return new Promise(resolve => {
      const reader = new FileReader();
      reader.onloadend = async () => {
        const base64 = reader.result;
        if (!base64) {
          resolve(null);
          return;
        }

        const img = new window.Image();
        img.src = base64.toString();
        img.onload;
        resolve({
          file: file,
          fileUrl: reader.result,
          width: img.width,
          height: img.height,
          previewLocalUrl: URL.createObjectURL(file),
          isChanged: true,
          hasChanged: true
        });
      };
      reader.readAsDataURL(file);
    })
    };

    
  const getPresetLength = (blockType: BlockTypes) => {
      switch (blockType) {
        case BlockTypes.colorNames: return ColorNamesTextBlockNum;
        case BlockTypes.graphicNames: return GraphicNamesTextBlockNum;
        case BlockTypes.graphicNames12cut: return GraphicNames12CutTextBlockNum;
        default: return 0;
      }
    }

  return (
    <Container className="border rounded p-2 bg-white">
      {isShowTextColorModal && (
        <ModalTextColorPreset
          onClickCancel={() => {
            setIsShowTextColorModal(false);
          }}
          onClickCreate={(model) => {
            if (isCreateTextPreset) {
              onPresetCreate(model);
            } else {
              onPresetEdit(textColorPresetIndex, model);
            }
            
            setIsShowTextColorModal(false);
          }}
          presetEditModel={textColorPreset}
          blockType={blockType}
          presetLength={getPresetLength(blockType)}
          isShow={isShowTextColorModal}
        />
        )}
      <Row className="p-2">
        <CloseButton className="ms-auto" onClick={deleteBtnClicked}/>
      </Row>
      <Row>
        <Col sm={6}>
          <Form.Group as={Row}>
            <Col sm={6} className="mb-2">
              <Form.Group className="d-flex justify-content-between align-items-center">
                <Form.Label>FRONT PRINT</Form.Label>
                <Form.Control
                  id={`originFile:${previewImage.id}`}
                  type="file"
                  style={{display: "none"}}
                  onChange={async (e: React.ChangeEvent<HTMLInputElement>) => {
                    const result = await handleFileOnChange(e);
                    if (result) {
                      updatePreviewImage(previewImage.id, { originFile: result?.file, originUrl: result.fileUrl })
                    }
                  }}
                />
                <Form.Label htmlFor={`originFile:${previewImage.id}`}>
                  <Button size="sm" variant="outline-secondary" as="span">{t("change")}</Button>
                </Form.Label>
              </Form.Group>
              <Image
                src={previewImage.originUrl}
                width={150}
                height={150}
                style={{objectFit: "contain"}}
                className="border rounded"
                />
            </Col>
            <Col sm={6} className="mb-2" hidden={presetConfig?.isPhotoCardSilverImagesConfigurable !== true}>
              <Form.Group className="d-flex justify-content-between align-items-center">
                <Form.Label>FRONT SILVER</Form.Label>
                <Form.Control
                  id={`silverFile:${previewImage.id}`}
                  type="file"
                  style={{display: "none"}}
                  onChange={async (e: React.ChangeEvent<HTMLInputElement>) => {
                    const result = await handleFileOnChange(e);
                    if (result) {
                      updatePreviewImage(previewImage.id, { silverFile: result?.file, silverUrl: result.fileUrl })
                    }
                  }}
                />
                <Form.Label htmlFor={`silverFile:${previewImage.id}`}>
                  <Button size="sm" variant="outline-secondary" as="span">{t("change")}</Button>
                </Form.Label>
              </Form.Group>
              <Image
                src={previewImage.silverUrl}
                width={150}
                height={150}
                style={{objectFit: "contain"}}
                className="border rounded"
                />
            </Col>
            <Col sm={6} className="mb-2" hidden={presetConfig?.isPhotoCardImagesConfigurable !== true}>
              <Form.Group className="d-flex justify-content-between align-items-center">
                <Form.Label>BACK PRINT</Form.Label>
                <Form.Control
                  id={`backOriginFile:${previewImage.id}`}
                  type="file"
                  style={{display: "none"}}
                  onChange={async (e: React.ChangeEvent<HTMLInputElement>) => {
                    const result = await handleFileOnChange(e);
                    if (result) {
                      updatePreviewImage(previewImage.id, { backOriginFile: result?.file, backOriginUrl: result.fileUrl })
                    }
                  }}
                />
                <Form.Label htmlFor={`backOriginFile:${previewImage.id}`}>
                  <Button size="sm" variant="outline-secondary" as="span">{t("change")}</Button>
                </Form.Label>
              </Form.Group>
              <Image
                src={previewImage.backOriginUrl}
                width={150}
                height={150}
                style={{objectFit: "contain"}}
                className="border rounded"
                />
            </Col>
            <Col sm={6} className="mb-2" hidden={presetConfig?.isPhotoCardSilverImagesConfigurable !== true}>
              <Form.Group className="d-flex justify-content-between align-items-center">
                <Form.Label>BACK SILVER</Form.Label>
                <Form.Control
                  id={`backSilverFile:${previewImage.id}`}
                  type="file"
                  style={{display: "none"}}
                  onChange={async (e: React.ChangeEvent<HTMLInputElement>) => {
                    const result = await handleFileOnChange(e);
                    if (result) {
                      updatePreviewImage(previewImage.id, { backSilverFile: result?.file, backSilverUrl: result.fileUrl })
                    }
                  }}
                />
                <Form.Label htmlFor={`backSilverFile:${previewImage.id}`}>
                  <Button size="sm" variant="outline-secondary" as="span">{t("change")}</Button>
                </Form.Label>
              </Form.Group>
              <Image
                src={previewImage.backSilverUrl}
                width={150}
                height={150}
                style={{objectFit: "contain"}}
                className="border rounded"
                />
            </Col>
            <Col sm={6} className="mb-2" hidden={presetConfig?.isCutImageConfigurable !== true}>
              <Form.Group className="d-flex justify-content-between align-items-center">
                <Form.Label>CUT</Form.Label>
                <Form.Control
                  id={`cutFile:${previewImage.id}`}
                  type="file"
                  style={{display: "none"}}
                  onChange={async (e: React.ChangeEvent<HTMLInputElement>) => {
                    const result = await handleFileOnChange(e);
                    if (result) {
                      updatePreviewImage(previewImage.id, { cutFile: result?.file, cutUrl: result.fileUrl })
                    }
                  }}
                />
                <Form.Label htmlFor={`cutFile:${previewImage.id}`}>
                  <Button size="sm" variant="outline-secondary" as="span">{t("change")}</Button>
                </Form.Label>
              </Form.Group>
              <Image
                src={previewImage.cutUrl}
                width={150}
                height={150}
                style={{objectFit: "contain"}}
                className="border rounded"
                />
            </Col>
            <Col sm={6} className="mb-2" hidden={presetConfig?.isPrevewImageConfigurable !== true}>
              <Form.Group className="d-flex justify-content-between align-items-center">
                <Form.Label>PREVIEW</Form.Label>
                <Form.Control
                  id={`previewFile:${previewImage.id}`}
                  type="file"
                  style={{display: "none"}}
                  onChange={async (e: React.ChangeEvent<HTMLInputElement>) => {
                    const result = await handleFileOnChange(e);
                    if (result) {
                      updatePreviewImage(previewImage.id, { previewFile: result?.file, previewUrl: result.fileUrl })
                    }
                  }}
                />
                <Form.Label htmlFor={`previewFile:${previewImage.id}`}>
                  <Button size="sm" variant="outline-secondary" as="span">{t("change")}</Button>
                </Form.Label>
              </Form.Group>
              <Image
                src={previewImage.previewUrl}
                width={150}
                height={150}
                style={{objectFit: "contain"}}
                className="border rounded"
                />
            </Col>
            <Col sm={6} className="mb-2" hidden={presetConfig?.isIconImageConfigurable !== true}>
              <Form.Group className="d-flex justify-content-between align-items-center">
                <Form.Label>ICON</Form.Label>
                <Form.Control
                  id={`iconFile:${previewImage.id}`}
                  type="file"
                  style={{display: "none"}}
                  onChange={async (e: React.ChangeEvent<HTMLInputElement>) => {
                    const result = await handleFileOnChange(e);
                    if (result) {
                      updatePreviewImage(previewImage.id, { iconFile: result?.file, iconUrl: result.fileUrl })
                    }
                  }}
                />
                <Form.Label htmlFor={`iconFile:${previewImage.id}`}>
                  <Button size="sm" variant="outline-secondary" as="span">{t("change")}</Button>
                </Form.Label>
              </Form.Group>
              <Image
                src={previewImage.iconUrl}
                width={150}
                height={150}
                style={{objectFit: "contain"}}
                className="border rounded"
                />
            </Col>
          </Form.Group>
        </Col>
        <Col sm={6}>
          <Form.Group>
            <FloatingLabel label={t("name")} className="mb-2">
              <Form.Control
                type="text"
                value={previewImage?.name}
                onChange={(e) => updatePreviewImage(previewImage.id, { name: e.target.value })}
                placeholder={t("name")}
              />
            </FloatingLabel>
          </Form.Group>
          <Form.Group hidden={presetConfig?.isPriorityConfigurable !== true}>
            <FloatingLabel label={t("priority")} className="mb-2">
              <Form.Control
                type="number"
                min={1}
                defaultValue={3}
                onChange={(e) => updatePreviewImage(previewImage.id, { priority: parseInt(e.target.value, 10) })}
              />
            </FloatingLabel>
          </Form.Group>
          <Form.Group hidden={!isColorNamesType}>
            <FloatingLabel label={t("colornames-layout")} className="mb-2">
              <Form.Select
                value={colorNamesLayout ?? CNSSetLayoutStyles.NONE}
                onChange={(e) => onChangeColorNamesLayout(e.target.value)}
              >
                {Object.values(CNSSetLayoutStyles).map((value: CNSSetLayoutStyles, index: number) =>
                  <option key={index}>{value}</option>
                )}
              </Form.Select>
            </FloatingLabel>
          </Form.Group>
          <Dropdown as={ButtonGroup} className="mb-2" hidden={!isUseTextColorPresets}>
            <Button
              variant="outline-secondary"
              onClick={() => {
                setTextColorPreset(undefined);
                setIsCreateTextPreset(true);
                setIsShowTextColorModal(true);
              }}
            >
              {t("add-color-preset")} ({(previewImage.switchableBlockModel as ColorNamesBlockModel)?.presetModels?.length ?? 0})
            </Button>
            <Dropdown.Toggle split variant="outline-secondary"/>
            <Dropdown.Menu>
              {(previewImage.switchableBlockModel as ColorNamesBlockModel)?.presetModels?.map((value, index) => (
                <Dropdown.Item 
                  key={index} 
                  className="d-flex justify-content-between align-items-center"
                >
                  <span
                    onClick={() => {
                      setTextColorPreset(value);
                      setTextColorPresetIndex(index);
                      setIsCreateTextPreset(false);
                      setIsShowTextColorModal(true);
                    }}>{value?.presetName}</span>
                  <CloseButton 
                    variant="danger" 
                    onClick={() => onPresetDelete(index)}
                  />
                </Dropdown.Item>
              ))}
            </Dropdown.Menu>
          </Dropdown>
          {previewImage.cutFile && <div style={{ margin: 10 }}>
            <span>{t("cut-file")} </span>
            <span>{previewImage.cutFile.name}</span>
          </div>}
        </Col>
      </Row>
    </Container>
  );
}

export default BlockPreviewImageBox;